import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { getUser } from "@/lib/user";
import "./FodindinLoveRegister.css";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api/fodindinlove/register-step1`;

export default function FodindinLoveRegister() {
  const navigate = useNavigate();
  const user = getUser();
  const [form, setForm] = useState({
    gender: "",
    lookingFor: "",
    minAge: 25,
    maxAge: 45,
    acceptTerms: false,
  });
  const [status, setStatus] = useState({ loading: false, error: "" });

  function update(field, value) {
    setForm((current) => ({ ...current, [field]: value }));
  }

  async function handleSubmit(event) {
    event.preventDefault();
    setStatus({ loading: true, error: "" });
    try {
      const response = await fetch(API_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, profile_id: user?.id || "" }),
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.detail || "Impossible de continuer.");
      }
      navigate(data.nextStep || "/abonnement?cat=love");
    } catch (error) {
      setStatus({ loading: false, error: error.message || "Une erreur est survenue." });
    }
  }

  return (
    <main className="fl-page" data-testid="fodindinlove-register-page">
      <header className="fl-header">
        <Link to="/" className="fl-brand" data-testid="fl-brand">
          <span className="fl-brand-heart">♥</span>
          <span>
            Fodindin<span>Love</span>
          </span>
        </Link>
        <Link to="/inscription" className="fl-login" data-testid="fl-login-link">
          Pas encore de compte ? Inscription
        </Link>
      </header>

      <section className="fl-card">
        <div className="fl-kicker">Une belle rencontre commence ici</div>
        <h1>Faisons connaissance</h1>
        <p className="fl-intro">
          Quelques choix pour découvrir des profils qui correspondent à ta recherche.
        </p>

        {!user ? (
          <div>
            <p className="fl-error" role="alert" data-testid="fl-no-account">
              Crée d'abord ton compte ou connecte-toi pour continuer.
            </p>
            <Link to="/inscription" className="fl-submit" style={{ display: "block", textAlign: "center", textDecoration: "none", marginTop: 14 }} data-testid="fl-create-account">
              Créer mon compte →
            </Link>
          </div>
        ) : (
          <form onSubmit={handleSubmit} data-testid="fl-form">
            <fieldset className="fl-fieldset">
              <legend>Je suis</legend>
              <div className="fl-options">
                {[
                  ["femme", "Une femme"],
                  ["homme", "Un homme"],
                  ["autre", "Autre / je préfère préciser plus tard"],
                ].map(([value, label]) => (
                  <label className={`fl-option ${form.gender === value ? "selected" : ""}`} key={value}>
                    <input
                      type="radio"
                      name="gender"
                      value={value}
                      checked={form.gender === value}
                      onChange={() => update("gender", value)}
                      data-testid={`fl-gender-${value}`}
                      required
                    />
                    <span>{label}</span>
                  </label>
                ))}
              </div>
            </fieldset>

            <fieldset className="fl-fieldset">
              <legend>Je recherche</legend>
              <div className="fl-options">
                {[
                  ["femme", "Une femme"],
                  ["homme", "Un homme"],
                  ["tous", "Tous les profils"],
                ].map(([value, label]) => (
                  <label className={`fl-option ${form.lookingFor === value ? "selected" : ""}`} key={value}>
                    <input
                      type="radio"
                      name="lookingFor"
                      value={value}
                      checked={form.lookingFor === value}
                      onChange={() => update("lookingFor", value)}
                      data-testid={`fl-lookingfor-${value}`}
                      required
                    />
                    <span>{label}</span>
                  </label>
                ))}
              </div>
            </fieldset>

            <fieldset className="fl-fieldset fl-age">
              <legend>Âge des profils recherchés</legend>
              <div className="fl-age-inputs">
                <label>
                  De
                  <select value={form.minAge} onChange={(e) => update("minAge", Number(e.target.value))} data-testid="fl-min-age">
                    {Array.from({ length: 63 }, (_, i) => i + 18).map((age) => (
                      <option key={age} value={age}>{age} ans</option>
                    ))}
                  </select>
                </label>
                <span className="fl-age-separator">à</span>
                <label>
                  À
                  <select value={form.maxAge} onChange={(e) => update("maxAge", Number(e.target.value))} data-testid="fl-max-age">
                    {Array.from({ length: 63 }, (_, i) => i + 18).map((age) => (
                      <option key={age} value={age}>{age} ans</option>
                    ))}
                  </select>
                </label>
              </div>
            </fieldset>

            <label className="fl-terms">
              <input
                type="checkbox"
                checked={form.acceptTerms}
                onChange={(e) => update("acceptTerms", e.target.checked)}
                data-testid="fl-accept-terms"
                required
              />
              <span>
                J'accepte les{" "}
                <a href="/conditions-generales" target="_blank" rel="noreferrer">Conditions générales</a>{" "}
                et j'ai lu la{" "}
                <a href="/confidentialite" target="_blank" rel="noreferrer">Politique de confidentialité</a>.
              </span>
            </label>

            <p className="fl-privacy-note">
              Tes préférences servent à personnaliser les profils proposés. Tu pourras les modifier dans ton compte.
            </p>

            {status.error && (
              <p className="fl-error" role="alert" data-testid="fl-error">{status.error}</p>
            )}

            <button className="fl-submit" type="submit" disabled={status.loading} data-testid="fl-submit">
              {status.loading ? "Enregistrement…" : "Continuer"}
              {!status.loading && <span aria-hidden="true"> →</span>}
            </button>

            <p className="fl-small">
              En continuant, tu confirmes que tu as l'âge requis pour utiliser le service,
              conformément aux Conditions générales.
            </p>
          </form>
        )}
      </section>

      <footer className="fl-footer">
        Des rencontres authentiques, à ton rythme. <span>♥</span>
      </footer>
    </main>
  );
}
